import * as React from "react";
import { editIncomeDrawdownDetailsLabels, editIncomeDrawdownDetailsMessages } from "../../Resource/Contents";
import IncomeRequired from "../../../Common/SIPP/IncomePaymentDetails/IncomeRequired";
import RegularIncomePayment from "../../../Common/SIPP/IncomePaymentDetails/RegularIncomePayment";
import IncomeFromPension from "./IncomeFromPension";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { ChooseAssetsToSellTable } from "../../../Common/SIPP/DrawdownDetails/ChooseAssetsToSellTable";
import { getChooseAssetsToSellTable } from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { SectionHeader } from "../../../../../Components/Clients/NewBusinessWizard/Components/SharedComponents";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { ChooseAssetsToSellFor } from "../../../Common/SIPP/DrawdownDetails/Resource/Contents";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { processTypeEnum } from "../../../Common/Resource/Contents";
import { setValueByKey } from "../../../../../../Shared/Table/Utils/TableUtils";
import { calculateAnnualIncomeAmount } from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";

interface IIncomePaymentProps extends IProcessComponentProps {
    data: PensionContribution;
    onChange: (changes) => void;
    onIncomeChange: (changes) => void;
    isITFC: boolean;
}

interface IIncomePaymentState {
    showAssetTable: boolean;
}

export class IncomePayment extends ProcessComponent<IIncomePaymentProps, IIncomePaymentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            showAssetTable: !this.renderAssetRadioButton(props.data),
        };
    }

    private hasDrawdown(data: PensionContribution): boolean {
        return !!data?.DrawdownModel?.InstructionId;
    }

    private hasTransfer(data: PensionContribution): boolean {
        return data?.PensionTransfers?.length > 0;
    }

    private isShowIncomeFromPension(data: PensionContribution) {
        return (
            data?.DrawdownModel?.Income?.TargetSpecificArrangements ||
            (data?.PensionTransfers?.length > 0 &&
                data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.TargetSpecificArrangements)
        );
    }

    handleAllocationChange(changes) {
        const { onChange, data } = this.props;
        let _data: typeof data = JSON.parse(JSON.stringify(data));
        if (this.hasDrawdown(data)) {
            for (const key in changes) {
                _data = setValueByKey(_data, `DrawdownModel.${key}`, changes[key]);
            }
        }
        if (this.hasTransfer(data)) {
            _data.PensionTransfers = _data?.PensionTransfers?.map((e) => {
                for (const key in changes) {
                    e = setValueByKey(e, `IncomePaymentDetails.DrawdownIncome.${key}`, changes[key]);
                }
                return e;
            });
        }
        onChange(_data);
    }

    renderAssetRadioButton(data: PensionContribution) {
        const drawdownAsset = data?.DrawdownModel?.IncomeDisinvestmentAllocations?.length > 0;
        const transferAsset =
            data?.PensionTransfers?.length > 0 &&
            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.IncomeDisinvestmentAllocations?.length > 0;
        return !(drawdownAsset || transferAsset);
    }

    public render(): JSX.Element {
        const { data, onChange, showErrorMsgPopUp, onIncomeChange, isITFC } = this.props;
        return (
            <>
                <IncomeRequired
                    data={
                        data?.DrawdownModel?.Income ||
                        (data?.PensionTransfers?.length > 0 &&
                            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome)
                    }
                    onChange={(changes) => this.onIncomeChangeOverride(changes, onIncomeChange)}
                    labels={editIncomeDrawdownDetailsLabels}
                    messages={editIncomeDrawdownDetailsMessages}
                    onRef={(ref) => this.appendChild(ref)}
                    drawdownType={data?.DrawdownModel?.DrawdownType}
                    pensionTransfer={data?.PensionTransfers}
                    regularCrystalDifferentInital={
                        data?.DrawdownModel?.RegularCrystallisationDetails?.HasADifferentInitialAmount
                    }
                    pensionBase={data}
                    processType={processTypeEnum.NewBusiness}
                    isITFC={isITFC}
                />
                <hr />
                <IncomeFromPension
                    data={data}
                    showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                    onChange={(changes) => onChange(changes)}
                    onRef={(ref) => this.appendChild(ref)}
                    isITFC={isITFC}
                />
                <RegularIncomePayment
                    data={
                        data?.DrawdownModel?.Income ||
                        (data?.PensionTransfers?.length > 0 &&
                            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome)
                    }
                    onChange={(changes) => this.onIncomeChangeOverride(changes, onIncomeChange)}
                    labels={editIncomeDrawdownDetailsLabels}
                    messages={editIncomeDrawdownDetailsMessages}
                    onRef={(ref) => this.appendChild(ref)}
                    drawdownType={data?.DrawdownModel?.DrawdownType}
                    availableCash={data?.DrawdownModel?.CrystallisationDetails?.AvailableCash?.Amount}
                    taxFreeLumpSum={data?.DrawdownModel?.TaxFreeLumpSum?.Amount}
                    regularCrystallisationDetails={data?.DrawdownModel?.RegularCrystallisationDetails}
                    shouldHidePaymentMethod={true}
                    pensionBase={data}
                    processType={processTypeEnum.NewBusiness}
                    isITFC={isITFC}
                    sellableAssets={getChooseAssetsToSellTable(data)}
                />
                {data?.DrawdownModel?.DrawdownType !== DrawdownType.Regular &&
                    SectionHeader("Choose assets to sell to pay income")}
                {data?.DrawdownModel?.DrawdownType !== DrawdownType.Regular && (
                    <>
                        <p>
                            Disinvestment from post-retirement to meet income payments
                            <span>
                                (
                                {MoneyDisplayHelper.getMoneyFormat({
                                    Amount: data?.DrawdownModel?.Income?.TargetSpecificArrangements
                                        ? data?.DrawdownModel?.Income?.GrossIncomePerPaymentFromPension?.Amount ||
                                          (
                                              data?.PensionTransfers?.length > 0 &&
                                              data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome
                                                  ?.AnnualIncome
                                          )?.GrossIncomePerPaymentFromPension?.Amount
                                        : data?.DrawdownModel?.Income?.GrossIncomePerPayment?.Amount ||
                                          (
                                              data?.PensionTransfers?.length > 0 &&
                                              data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome
                                                  ?.AnnualIncome
                                          )?.GrossIncomePerPayment?.Amount,
                                    Currency: CurrencyCode.GBP,
                                })}
                                )
                            </span>
                        </p>
                        <RadiosEditor
                            options={[true, false]}
                            label={"Choose asset selection method"}
                            getText={(v) => (v ? "Proportionally across all assets" : "From specified assets")}
                            onChange={(v) => {
                                this.handleAllocationChange({
                                    IncomeDisinvestmentAllocations: [],
                                    IsAcrossAllAssets: v,
                                });
                                this.setState({ showAssetTable: !v });
                            }}
                            tooltip={
                                "If there is insufficient value in an asset at the time of the sale, we will default to disinvesting proportioanlly from the assets selected."
                            }
                            cssWrapper={"rg-inline mb-10"}
                            selected={this.renderAssetRadioButton(data)}
                        />
                    </>
                )}
                {this.state.showAssetTable && (
                    <ChooseAssetsToSellTable
                        modelData={data?.DrawdownModel}
                        data={
                            data?.DrawdownModel?.IncomeDisinvestmentAllocations ||
                            (data?.PensionTransfers?.length > 0 &&
                                data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome
                                    ?.IncomeDisinvestmentAllocations)
                        }
                        tableData={getChooseAssetsToSellTable(data)}
                        onRef={(ref) => this.appendChild(ref)}
                        onChange={(v) => {
                            this.handleAllocationChange({ IncomeDisinvestmentAllocations: v });
                        }}
                        reason={ChooseAssetsToSellFor.Income}
                        showErrorMsgPopUp={(msg, isShow) => showErrorMsgPopUp(msg, isShow)}
                        processType={ProcessType.NB}
                        transferData={data?.PensionTransfers}
                        pensionBase={data}
                    />
                )}
                <hr />
            </>
        );
    }

    private onIncomeChangeOverride = (changes, callback: (x) => void) => {
        const { data } = this.props;

        const annualIncome = data?.DrawdownModel?.Income;
        const grossIncomePerPayment = changes['GrossIncomePerPayment'] ?? annualIncome?.GrossIncomePerPayment;
        const incomeFrequency = changes['Frequency'] ?? annualIncome?.Frequency;

        if (grossIncomePerPayment?.Amount != null && grossIncomePerPayment?.Amount != undefined &&
            incomeFrequency != null && incomeFrequency != undefined) {
            changes.AnnualAmount = {
                Amount: calculateAnnualIncomeAmount(grossIncomePerPayment.Amount, incomeFrequency)
            };
        }
        callback(changes);
    }
}

export default IncomePayment;
